import { useCallback, useMemo } from 'react'
import { useGuildStatusUnguarded } from '@/services/GuildStatusService/GuildStatusService'
import { useUser } from '@/services/UserService'
import { Maybe, MoneyCurrencyCode, ReservationDiscount } from '@/types/codegen-federation'
import { useSafeTrack } from '@/utils/analytics'
import { getBooleanFromSessionStorage, getStringFromSessionStorage } from '@/utils/session-storage'
import { getIsAngelEmployeeUser } from '@/utils/users/users'

const PLATFORM = 'angel-web'

const PRODUCT_VIEWED = 'Product Viewed'
const PRODUCT_ADDED = 'Product Added'
const CHECKOUT_STARTED = 'Checkout Started'
const ORDER_COMPLETED = 'Order Completed'

export const ECOMMERCE_EVENTS = [PRODUCT_VIEWED, PRODUCT_ADDED, CHECKOUT_STARTED, ORDER_COMPLETED] as const

export type EcommFunnel = 'pif' | 'theatrical' | 'theatrical-pre-sale' | 'guild' | 'collectibles' | 'filmmaker'

export interface EcommPayload {
  funnel: EcommFunnel
  projectSlug?: string | null
  new_customer?: string | boolean
}

export interface Product {
  product_id: string
  sku: string
  category: string
  name: string
  brand?: string
  variant?: string
  price: number
  quantity: number
  coupon?: string
  position?: number
  url?: string | null
  image_url?: string
  currency: MoneyCurrencyCode
  /** @deprecated - used for PIF: delete after old tracking is removed */
  region?: string
}

interface ProductViewedPayload extends EcommPayload, Product {
  price: number
  value?: number // total value of the product after quantity
}

export interface ProductAddedPayload extends EcommPayload, Product {
  cart_id?: string
}

interface CheckoutStartedPayload extends EcommPayload {
  order_id?: string | null
  affiliation?: string
  checkout_id?: string | null
  value: number // revenue with discounts and coupons added in
  revenue?: number // excluding shipping and tax
  shipping?: number
  tax?: number
  discount?: number
  coupon?: string
  currency: MoneyCurrencyCode
  products?: Product[]
  subscription?: boolean
}

export interface OrderCompletedPayload extends EcommPayload {
  email: string
  checkout_id?: string | null
  order_id: string
  affiliation?: string | null
  subtotal?: number
  total: number
  revenue?: number
  shipping?: number
  tax?: number
  discount?: number
  coupon?: string
  currency: MoneyCurrencyCode
  products?: Product[]
  referring_user_id?: string | null
  subscription?: boolean
  /** @deprecated - used for PIF: delete after old tracking is removed */
  category?: string
  /** @deprecated - used for PIF: delete after old tracking is removed */
  region?: string
  /** @deprecated - used for PIF: delete after old tracking is removed */
  project?: string | null
  /** @deprecated - used for PIF: delete after old tracking is removed */
  payment_method?: string | null

  payment_gateway?: 'stripe' | 'paypal' | null | undefined
}

function getCoupons(discounts?: Maybe<ReservationDiscount[]>) {
  if (!discounts) return undefined

  return discounts.reduce((prev, curr) => {
    return prev + curr.code + ','
  }, '')
}

function getGuildEcommerceProductIdFromStorage(): string | null {
  if (typeof window === 'undefined') return null
  return getStringFromSessionStorage('guildEcommerceProductId')
}

function getProductIdForGuild(productId: string, funnel: EcommFunnel): string {
  if (funnel === 'guild') {
    const guildEcommerceProductId = getGuildEcommerceProductIdFromStorage()
    return guildEcommerceProductId || productId
  }
  return productId
}

function getTheatricalLiteVariant(funnel: EcommFunnel) {
  if (funnel !== 'theatrical' && funnel !== 'theatrical-pre-sale') return {}
  if (typeof window === 'undefined') return {}
  if (getBooleanFromSessionStorage('theatricalLiteVariant')) {
    return { experiment_theatrical_lite_variant: 'variant' }
  }
  return {}
}
/**
 * Hook for tracking ecommerce events.
 * https://www.notion.so/angelstudios/Segment-Ecom-Events-0f7e8222f783493f8ccfd7c6d17ef8f7
 */
export const useEcommerceEvents = () => {
  const track = useSafeTrack()
  const { isLoggedIn, user, error: userError } = useUser()
  const guildStatus = useGuildStatusUnguarded()

  const new_customer = useMemo(() => {
    // If there's an error fetching user data, don't include new_customer
    if (userError) {
      return undefined
    }
    if (!isLoggedIn) {
      return undefined
    }
    // If guildStatus is not available, return undefined
    if (!guildStatus) {
      return undefined
    }
    // If logged in and is an Angel employee, they are an existing customer
    if (getIsAngelEmployeeUser(user?.email)) {
      return false
    }
    // If logged in and NOT a guild member, they are a new customer
    // If logged in and IS a guild member, they are an existing customer
    return !guildStatus.isGuildMember
  }, [isLoggedIn, user?.email, guildStatus, userError])

  /** Fire this event when a visitor views a product. That view might happen on a page, screen, or preview modal. */
  const trackProductViewed = useCallback(
    (payload: ProductViewedPayload) =>
      track(PRODUCT_VIEWED, {
        ...payload,
        platform: PLATFORM,
        ...(payload.funnel === 'guild' && new_customer !== undefined && { new_customer }),
        ...getTheatricalLiteVariant(payload.funnel),
        value: Math.max(0.01, payload.value ?? payload.price ?? 0),
        price: Math.max(0.01, payload.price ?? 0),
        product_id: getProductIdForGuild(payload.product_id, payload.funnel),
      }),
    [track, new_customer],
  )

  /** Fire this event when a visitor adds a product to their shopping cart or click cta for a product that takes them to checkout. */
  const trackProductAdded = useCallback(
    ({ price, ...payload }: ProductAddedPayload) =>
      track(PRODUCT_ADDED, {
        ...payload,
        platform: PLATFORM,
        ...(payload.funnel === 'guild' && new_customer !== undefined && { new_customer }),
        ...getTheatricalLiteVariant(payload.funnel),
        price: Math.max(0.01, price ?? 0),
        product_id: getProductIdForGuild(payload.product_id, payload.funnel),
      }),
    [track, new_customer],
  )

  /** Fire this event whenever an order/transaction was started.
   * Fire on the page that the customer lands on after they press the checkout button. */
  const trackCheckoutStarted = useCallback(
    (payload: CheckoutStartedPayload) =>
      track(CHECKOUT_STARTED, {
        ...payload,
        platform: PLATFORM,
        ...(payload.funnel === 'guild' && new_customer !== undefined && { new_customer }),
        ...getTheatricalLiteVariant(payload.funnel),
        revenue: Math.max(0.01, payload.revenue ?? payload.value ?? 0),
        value: Math.max(0.01, payload.value ?? 0),
        products: payload.products?.map((product) => ({
          ...product,
          product_id: getProductIdForGuild(product.product_id, payload.funnel),
        })),
      }),
    [track, new_customer],
  )

  /** Fire this event whenever an order/transaction was successfully completed by the customer. */
  const trackOrderCompleted = useCallback(
    (payload: OrderCompletedPayload, options?: unknown | null, callback?: (...params: unknown[]) => void) =>
      track(
        ORDER_COMPLETED,
        {
          ...payload,
          platform: PLATFORM,
          ...(payload.funnel === 'guild' && new_customer !== undefined && { new_customer }),
          ...getTheatricalLiteVariant(payload.funnel),
          revenue: Math.max(0.01, payload.revenue ?? payload.total ?? 0),
          total: Math.max(0.01, payload.total ?? 0),
          value: Math.max(0.01, payload.total ?? 0),
          products: payload.products?.map((product) => ({
            ...product,
            product_id: getProductIdForGuild(product.product_id, payload.funnel),
          })),
        },
        options,
        callback,
      ),
    [track, new_customer],
  )

  return {
    getCoupons,
    trackProductViewed,
    trackProductAdded,
    trackCheckoutStarted,
    trackOrderCompleted,
  }
}
